import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { countdownParts, useDealRemainingMs } from '../../lib/dealCountdown';
import styles from './DealCountdown.module.css';

interface DealCountdownProps {
  endsAt: string;
  clockOffsetMs?: number;
  className?: string;
  compact?: boolean;
}

export function DealCountdown({
  endsAt,
  clockOffsetMs = 0,
  className,
  compact = false,
}: DealCountdownProps) {
  const remaining = useDealRemainingMs(endsAt, clockOffsetMs);
  const reducedMotion = useReducedMotion();
  if (remaining <= 0) return null;

  return (
    <span
      className={[styles.countdown, compact ? styles.compact : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      aria-label={`Deal ends in ${countdownParts(remaining)
        .map((part) => `${part.value}${part.suffix}`)
        .join(' ')}`}
    >
      {countdownParts(remaining).map((part, index) => (
        <span className={styles.part} key={part.suffix} aria-hidden>
          {index > 0 ? <span className={styles.separator}>:</span> : null}
          <span className={styles.digits}>
            {part.value.split('').map((digit, position) => (
              <span className={styles.valueClip} key={position}>
                <AnimatePresence initial={false} mode="popLayout">
                  <motion.span
                    // Position stays mounted; only the character changing at
                    // that position gets a new key and performs the roll.
                    key={digit}
                    className={styles.value}
                    initial={reducedMotion ? false : { y: -8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={reducedMotion ? undefined : { y: 8, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {digit}
                  </motion.span>
                </AnimatePresence>
              </span>
            ))}
          </span>
          <span className={styles.suffix}>{part.suffix}</span>
        </span>
      ))}
    </span>
  );
}
